import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useState } from 'react';
import { Category, CreateRadioStationRequest, RadioStation } from '@/types/model';
import { useTranslation } from '@/hooks';
import { useRadioCategoriesForDropdown } from '@/hooks';
import { Translation } from '@/locales';
import Input from '@/components/ui/Input';
import Toggle from '@/components/ui/Toggle';
import Button from '@/components/ui/Button';
import MultiSelect from '@/components/ui/MultiSelect';
import ImageNotAvailable from '@/assets/image-not-available.png';
import CountryDropdown from '@/components/ui/CountryDropdown';
import ImageUrlUploadField from '@/components/ui/ImageUrlUploadField';
import { MediaSource } from '@/types/media';
import { useBranding } from '@/context/BrandingContext';

const createRadioStationSchema = (t: Translation) => z.object({
  stationName: z
    .string()
    .min(1, t.validation.minLength(1))
    .max(255, t.validation.maxLength(255)),
  code: z
    .string()
    .min(1, "El código es requerido")
    .max(50, t.validation.maxLength(50)),
  stationLogo: z
    .url({
      message: t.radioStations.validation.stationLogoInvalid
    })
    .or(z.literal('')),
  countryId: z
    .string()
    .optional(),
  categories: z
    .array(z.string())
    .optional(),
  streamUrl: z
    .url({
      message: t.radioStations.validation.streamUrlInvalid
    })
    .min(1, t.radioStations.validation.streamUrlRequired),
  active: z.boolean(),
});

type RadioStationFormData = z.infer<ReturnType<typeof createRadioStationSchema>>;

interface RadioStationFormProps {
  onSuccess?: (data: CreateRadioStationRequest) => void;
  onCancel?: () => void;
  initialData?: Partial<CreateRadioStationRequest>;
  isEdit?: boolean;
  isSubmitting?: boolean;
}

const RadioStationForm = ({
  onSuccess,
  onCancel,
  initialData,
  isEdit = false,
  isSubmitting = false
}: RadioStationFormProps) => {
  const { t } = useTranslation();
  const { branding } = useBranding();
  const showCountryField = branding.modules?.radioCountriesEnabled === true;
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [selectedCategories, setSelectedCategories] = useState<string[]>(initialData?.categories || []);
  const { data: categoriesData } = useRadioCategoriesForDropdown();
  const initialCountryId = initialData?.countryId || ((initialData as RadioStation | undefined)?.country?.id);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset,
    control
  } = useForm<RadioStationFormData>({
    resolver: zodResolver(createRadioStationSchema(t)),
    defaultValues: {
      stationName: initialData?.stationName || '',
      code: initialData?.code || '',
      stationLogo: initialData?.stationLogo || '',
      countryId: initialCountryId || undefined,
      categories: initialData?.categories || [],
      streamUrl: initialData?.streamUrl || '',
      active: initialData?.active ?? true,
    }
  });

  const onSubmit = async (data: RadioStationFormData) => {
    setSubmitError(null);

    try {
      const requestData: CreateRadioStationRequest = {
        ...data
      };
      await onSuccess?.(requestData);
      if (!isEdit) {
        reset();
      }
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : t.errors.unexpectedError);
    }
  };

  const watchedValues = watch(['active', 'stationLogo']);
  const [active, stationLogo] = watchedValues;

  const mapCategoriesToOptions = (data: unknown[]) =>
    (data as Category[]).map(cat => ({ value: cat.id, label: cat.description }));

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900">
          {isEdit ? t.radioStations.editTitle : t.radioStations.createTitle}
        </h2>
        <p className="text-gray-600 mt-1">
          {isEdit ? t.radioStations.editDescription : t.radioStations.createDescription}
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="flex gap-6">
          <div className="flex-1 space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <Input
                  label="Código"
                  placeholder="Código de la estación"
                  error={errors.code?.message}
                  {...register('code')}
                />
              </div>
              {showCountryField && (
                <div>
                  <Controller
                    name="countryId"
                    control={control}
                    render={({ field }) => (
                      <CountryDropdown
                        label={t.radioStations.fields.country}
                        placeholder={t.radioStations.placeholders.country}
                        error={errors.countryId?.message}
                        disabled={isSubmitting}
                        {...field}
                        value={field.value ?? ''}
                        onChange={(value) => field.onChange(value)}
                      />
                    )}
                  />
                </div>
              )}
            </div>

            <div className="md:col-span-2">
              <Input
                label={t.radioStations.fields.stationName}
                placeholder={t.radioStations.placeholders.stationName}
                error={errors.stationName?.message}
                {...register('stationName')}
              />
            </div>

            <ImageUrlUploadField
              label={t.radioStations.fields.stationLogo}
              placeholder={t.radioStations.placeholders.stationLogo}
              value={stationLogo || ''}
              onChange={(url) => setValue('stationLogo', url, { shouldValidate: true, shouldDirty: true })}
              error={errors.stationLogo?.message}
              disabled={isSubmitting}
              source={MediaSource.RADIO_IMAGE}
              showPreview={false}
            />

            <MultiSelect
              label={t.radioStations.fields.categories}
              placeholder={t.radioStations.placeholders.categories}
              options={mapCategoriesToOptions(categoriesData || [])}
              value={selectedCategories}
              onChange={(newCategories) => {
                setSelectedCategories(newCategories);
                setValue('categories', newCategories);
              }}
              error={errors.categories?.message}
            />
          </div>
          <div className="flex-shrink-0">
            {stationLogo ? (
              <img
                src={stationLogo}
                alt="Vista previa del logo"
                className="h-58 w-40 object-cover rounded-lg shadow-md"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = ImageNotAvailable;
                  target.style.display = 'block';
                }}
                onLoad={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'block';
                }}
              />
            ) : (
              <div className="h-58 w-40 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50">
                <div className="text-center text-gray-500">
                  <div className="text-xs">Vista previa</div>
                  <div className="text-xs">del logo</div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <Input
              label={t.radioStations.fields.streamUrl}
              type="url"
              placeholder={t.radioStations.placeholders.streamUrl}
              error={errors.streamUrl?.message}
              helperText="URL del stream de audio de la estación de radio"
              {...register('streamUrl')}
            />
          </div>

          <div className="md:col-span-2">
            <Toggle
              label={t.radioStations.fields.active}
              checked={active}
              onChange={(checked) => setValue('active', checked)}
              disabled={isSubmitting}
            />
          </div>
        </div>

        {submitError && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{submitError}</p>
          </div>
        )}

        <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
          {onCancel && (
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              {t.buttons.cancel}
            </Button>
          )}
          <Button
            type="submit"
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            {isSubmitting
              ? t.radioStations.actions.creating
              : isEdit
                ? 'Actualizar Estación de Radio'
                : t.radioStations.actions.create
            }
          </Button>
        </div>
      </form>
    </div>
  );
};

export default RadioStationForm;
